import apiService, { API_ENDPOINTS, HttpError } from "./api";

class ApiInterceptor {
  constructor() {
    this.isRefreshing = false;
    this.failedQueue = [];
  }

  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  clearSessionAndRedirect() {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    window.location.assign("/login");
  }

  async handleUnauthorized() {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;
    try {
      const newToken = await this.refreshToken();
      this.processQueue(null, newToken);
      return newToken;
    } catch (error) {
      this.processQueue(error, null);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      const result = await apiService.post(
        API_ENDPOINTS.AUTH.REFRESH,
        { refreshToken },
        { includeAuth: false },
      );

      if (result.success && result.data && result.data.accessToken) {
        localStorage.setItem("token", result.data.accessToken);
        if (result.data.refreshToken) {
          localStorage.setItem("refreshToken", result.data.refreshToken);
        }
        return result.data.accessToken;
      }
      throw new Error("Token refresh failed");
    } catch (error) {
      this.clearSessionAndRedirect();
      throw error;
    }
  }

  async requestWithRetry(endpoint, options = {}, retryCount = 0) {
    const maxRetries = 1;
    const includeAuth = options.includeAuth !== false;

    try {
      return await apiService.request(endpoint, options);
    } catch (error) {
      const unauthorized =
        error instanceof HttpError && error.status === 401 && includeAuth;

      if (unauthorized && retryCount < maxRetries) {
        await this.handleUnauthorized();
        return this.requestWithRetry(endpoint, options, retryCount + 1);
      }

      if (unauthorized && retryCount >= maxRetries) {
        this.clearSessionAndRedirect();
      }

      throw error;
    }
  }

  handleNetworkError(error) {
    if (!navigator.onLine) {
      throw new Error(
        "No internet connection. Please check your network and try again.",
      );
    }

    if (error.name === "TypeError" && error.message.includes("fetch")) {
      throw new Error(
        "Network error. Please check your connection and try again.",
      );
    }

    throw error;
  }

  logApiCall(method, endpoint, data = null) {
    if (process.env.REACT_APP_ENABLE_DEBUG === "true") {
      console.log(`[API] ${method.toUpperCase()} ${endpoint}`, data);
    }
  }

  logApiResponse(endpoint, response) {
    if (process.env.REACT_APP_ENABLE_DEBUG === "true") {
      console.log(`[API Response] ${endpoint}`, response);
    }
  }
}

const apiInterceptor = new ApiInterceptor();

export const enhancedApiService = {
  get: async (endpoint, options = {}) => {
    apiInterceptor.logApiCall("GET", endpoint);
    try {
      const response = await apiInterceptor.requestWithRetry(endpoint, {
        method: "GET",
        ...options,
      });
      apiInterceptor.logApiResponse(endpoint, response);
      return response;
    } catch (error) {
      apiInterceptor.handleNetworkError(error);
    }
  },

  post: async (endpoint, data, options = {}) => {
    apiInterceptor.logApiCall("POST", endpoint, data);
    try {
      const response = await apiInterceptor.requestWithRetry(endpoint, {
        method: "POST",
        body: JSON.stringify(data),
        ...options,
      });
      apiInterceptor.logApiResponse(endpoint, response);
      return response;
    } catch (error) {
      apiInterceptor.handleNetworkError(error);
    }
  },

  put: async (endpoint, data, options = {}) => {
    apiInterceptor.logApiCall("PUT", endpoint, data);
    try {
      const response = await apiInterceptor.requestWithRetry(endpoint, {
        method: "PUT",
        body: JSON.stringify(data),
        ...options,
      });
      apiInterceptor.logApiResponse(endpoint, response);
      return response;
    } catch (error) {
      apiInterceptor.handleNetworkError(error);
    }
  },

  patch: async (endpoint, data, options = {}) => {
    apiInterceptor.logApiCall("PATCH", endpoint, data);
    try {
      const response = await apiInterceptor.requestWithRetry(endpoint, {
        method: "PATCH",
        body: JSON.stringify(data),
        ...options,
      });
      apiInterceptor.logApiResponse(endpoint, response);
      return response;
    } catch (error) {
      apiInterceptor.handleNetworkError(error);
    }
  },

  delete: async (endpoint, options = {}) => {
    apiInterceptor.logApiCall("DELETE", endpoint);
    try {
      const response = await apiInterceptor.requestWithRetry(endpoint, {
        method: "DELETE",
        ...options,
      });
      apiInterceptor.logApiResponse(endpoint, response);
      return response;
    } catch (error) {
      apiInterceptor.handleNetworkError(error);
    }
  },

  upload: async (endpoint, formData, options = {}) => {
    apiInterceptor.logApiCall("UPLOAD", endpoint);
    try {
      const response = await apiInterceptor.requestWithRetry(endpoint, {
        method: "POST",
        body: formData,
        ...options,
      });
      apiInterceptor.logApiResponse(endpoint, response);
      return response;
    } catch (error) {
      apiInterceptor.handleNetworkError(error);
    }
  },
};

export { API_ENDPOINTS, API_CONFIG, HttpError } from "./api";
export default enhancedApiService;
